import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useDebug } from 'contexts/DebugContext';
import type { RoleplaySession } from 'services/api/roleplayClient';
import { RoleplayScreens } from './RoleplayScreens';
import { useRoleplaySession } from './useRoleplaySession';

export interface RoleplayFlowStepProps {
  flowName: string;
  flowRunId: number;
  questionId: number;
  /** True when this step was already completed in the current flow run. */
  canContinue?: boolean;
  onContinue: (session: RoleplaySession | null) => void | Promise<void>;
  onBack?: () => void;
}

/**
 * A roleplay question rendered as one step of an ordinary question flow.
 *
 * The flow shell owns progress, the header, and navigation between items. This
 * step only reports when the learner has a confirmed-win attempt (or the item
 * was already completed) and chooses to continue.
 */
const RoleplayFlowStep: React.FC<RoleplayFlowStepProps> = ({
  flowName,
  flowRunId,
  questionId,
  canContinue = false,
  onContinue,
  onBack,
}) => {
  const navigate = useNavigate();
  const { isEnabled: isDebugEnabled } = useDebug();
  const controller = useRoleplaySession(flowName, flowRunId, questionId);

  const handleContinue = useCallback(() => {
    void onContinue(controller.session);
  }, [controller.session, onContinue]);

  return (
    <RoleplayScreens
      controller={controller}
      variant="embedded"
      canContinue={canContinue}
      continueLabel="Continue"
      onContinue={handleContinue}
      onExit={() => navigate('/training#courses')}
      onSafetyClose={() => navigate('/home')}
      onBack={onBack}
      showDebugPanel={isDebugEnabled}
    />
  );
};

export default RoleplayFlowStep;
